import React, { useCallback, useEffect, useState } from "react";
import { useField } from "formik";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";

// Helper function to get nested values using dot notation
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const DropdownSelector = ({
  label,
  optionLabel,
  optionSeparator = " ",
  parentOptions,
  fetchFunction,
  fetchParams = null,
  keyField = "id",
  ...props
}) => {
  const [field, meta] = useField(props.name);
  const [options, setOptions] = useState([]);
  const { openSnack } = useSnack();

  const fetchData = useCallback(async () => {
    try {
      const res = await fetchFunction(fetchParams);
      setOptions(res.data);
    } catch (e) {
      openSnack(handleError(e), "error");
    }
  }, [fetchFunction, fetchParams, openSnack]);

  useEffect(() => {
    if (parentOptions) {
      setOptions(parentOptions);
    } else {
      fetchData();
    }
  }, [fetchFunction, fetchParams, parentOptions, fetchData]);

  const getOptionLabel = (option) => {
    if (Array.isArray(optionLabel)) {
      // If optionLabel is an array, join the values using optionSeparator
      return optionLabel
        .map((label) => option[label] || "")
        .join(optionSeparator);
    }
    if (typeof optionLabel === "string" && optionLabel.includes(".")) {
      return getNestedValue(option, optionLabel) || option.name;
    }
    // If optionLabel is a string, return the corresponding option value
    return option[optionLabel] || option.name;
  };

  const findOption = (value) =>
    options.find(
      (opt) =>
        opt[keyField] === value || String(opt[keyField]) === String(value)
    );

  return (
    <FormControl
      fullWidth
      variant="outlined"
      error={meta.touched && Boolean(meta.error)}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        {...field}
        {...props}
        value={field.value ?? ""}
        onChange={(event) => {
          field.onChange(event); // Invoke Formik's handler
          if (props.onChange) {
            props.onChange(event, findOption(event.target.value)); // Custom onChange
          }
        }}
        onBlur={field.onBlur}
      >
        {options &&
          Array.isArray(options) &&
          options.map((option) => (
            <MenuItem key={option[keyField]} value={option[keyField]}>
              {getOptionLabel(option)}
            </MenuItem>
          ))}
      </Select>
      {meta.touched && meta.error ? (
        <FormHelperText>{meta.error}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

export default DropdownSelector;
